import { AbstractService } from "./AbstractService";
import _ from "lodash";
import { ConfigurableInterface } from "./ConfigurableInterface";
import { WebexMeeting } from "../types";

export interface WebexServiceInterface extends ConfigurableInterface {
  init(
    callback: (webex: any) => void,
    errorCallback?: (error: string) => void
  ): void;
  getStatistic(
    meeting: WebexMeeting
  ): {
    audioReceive: {
      packetsLost: number[];
      jitter: number[];
      latency: number[];
      bitRate: number[];
    };
    audioTransmit: {
      packetsLost: number[];
      jitter: number[];
      latency: number[];
      bitRate: number[];
    };
    videoReceive: {
      packetsLost: number[];
      jitter: number[];
      latency: number[];
      bitRate: number[];
      frameRate: number[];
      resolutionWidth: number[];
      resolutionHeight: number[];
    };
    videoTransmit: {
      packetsLost: number[];
      jitter: number[];
      latency: number[];
      bitRate: number[];
      frameRate: number[];
      resolutionWidth: number[];
      resolutionHeight: number[];
    };
  };
}

export class WebexService extends AbstractService
  implements WebexServiceInterface {
  private readonly baseInitConfig = {
    logger: {
      // silent, group, groupEnd, error, warn, log, info, debug, trace
      level: "warn"
    },
    meetings: {
      aspectRatio: 16 / 9,
      deviceType: "WEB",
      reconnection: {
        enabled: true
      }
    },
    device: {
      ephemeral: true, // defaults to true, just to be sure
      ephemeralDeviceTTL: 30 * 60, // override default ttl of 30 mins
      body: {
        // Bug workaround:
        // unless specified here, ttl is not being sent for ephemeral
        // devices on POST /devices, only when refreshing them on PUT /devices
        ttl: 30 * 60
      }
    }
  };

  private startLoggingMeetingsEvents(meetings: any): void {
    meetings.on("all", (event: unknown) => {
      console.debug("Meetings [all]:", event);
    });
    meetings.on("error", (error: Error) => {
      console.error("Meetings:", error);
    });
  }

  private initializeOAuthVersion() {
    this.checkConfig(["webexClientId", "webexRedirectUri", "webexScope"]);

    console.info("Initializing Webex SDK");

    /* eslint-disable */
    // @ts-ignore
    return window.Webex.init({
      /* eslint-enable */
      config: _.assign(
        {
          credentials: {
            // eslint-disable-next-line @typescript-eslint/camelcase
            client_id: this.config.webexClientId,
            // eslint-disable-next-line @typescript-eslint/camelcase
            redirect_uri: this.config.webexRedirectUri,
            scope: this.config.webexScope
          }
        },
        this.baseInitConfig
      )
    });
  }

  private initializeTokenVersion() {
    this.checkConfig("webexAccessToken");

    console.info(
      "Initializing Webex SDK with token:",
      this.config.webexAccessToken
    );

    /* eslint-disable */
    // @ts-ignore
    return window.Webex.init({
      /* eslint-enable */
      config: this.baseInitConfig,
      credentials: {
        access_token: this.config.webexAccessToken // eslint-disable-line @typescript-eslint/camelcase
      }
    });
  }

  async init(
    callback: (webex: any) => void,
    errorCallback?: (error: string) => void
  ): Promise<void> {
    this.checkConfig("clientType");

    const webex =
      this.config.clientType === "kiosk"
        ? this.initializeTokenVersion()
        : this.initializeOAuthVersion();

    webex.once("ready", async () => {
      if (
        this.config.clientType === "expert" &&
        !webex.canAuthorize &&
        !this.config.webexWithoutLoginInitialization
      ) {
        // Initiate login to Webex account
        webex.authorization.initiateLogin();
      }

      if (
        this.config.clientType === "expert" &&
        !webex.canAuthorize &&
        this.config.webexWithoutLoginInitialization
      ) {
        callback(webex);
        return;
      }

      this.startLoggingMeetingsEvents(webex.meetings);

      const getErrorMessage = (error?: Error) => {
        if (!error) {
          return "Webex initialization error. Please, reload web page";
        }

        const [firstLine] = error.message.split("\n", 1);
        return firstLine;
      };

      try {
        await webex.meetings.register();
        console.log("Device successfully registered with Webex");
      } catch (error) {
        console.error("Unable to register device with Webex:", error);
        // For some reason WebEx throws error even if registration was successfully
        errorCallback?.(getErrorMessage(error));
        return;
      }

      try {
        await webex.meetings.syncMeetings();
        console.log("Webex meetings synchronized");
      } catch (error) {
        console.error("Unable to synchronize Webex meetings:", error);
        errorCallback?.(getErrorMessage(error));
        return;
      }

      callback(webex);
    });
  }

  public getStatistic(meeting: WebexMeeting) {
    return {
      audioReceive: _.get(meeting, "statsAnalyzer.localMQEStats.audio.RX") as {
        packetsLost: [];
        jitter: [];
        latency: [];
        bitRate: [];
      },
      audioTransmit: _.get(meeting, "statsAnalyzer.localMQEStats.audio.TX") as {
        packetsLost: [];
        jitter: [];
        latency: [];
        bitRate: [];
      },
      videoReceive: _.get(meeting, "statsAnalyzer.localMQEStats.video.RX") as {
        packetsLost: [];
        jitter: [];
        latency: [];
        bitRate: [];
        frameRate: [];
        resolutionWidth: [];
        resolutionHeight: [];
      },
      videoTransmit: _.get(meeting, "statsAnalyzer.localMQEStats.video.TX") as {
        packetsLost: [];
        jitter: [];
        latency: [];
        bitRate: [];
        frameRate: [];
        resolutionWidth: [];
        resolutionHeight: [];
      }
    };
  }
}

export class StagingWebexService extends AbstractService
  implements WebexServiceInterface {
  public async init(
    callback: (webex: any) => void,
    errorCallback?: (error: string) => void
  ): Promise<void> {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback({}), this.config.stagingRequestDuration);
  }

  public getStatistic(meeting: WebexMeeting) {
    return {
      audioReceive: {
        packetsLost: [],
        jitter: [],
        latency: [],
        bitRate: []
      },
      audioTransmit: {
        packetsLost: [],
        jitter: [],
        latency: [],
        bitRate: []
      },
      videoReceive: {
        packetsLost: [],
        jitter: [],
        latency: [],
        bitRate: [],
        frameRate: [],
        resolutionWidth: [],
        resolutionHeight: []
      },
      videoTransmit: {
        packetsLost: [],
        jitter: [],
        latency: [],
        bitRate: [],
        frameRate: [],
        resolutionWidth: [],
        resolutionHeight: []
      }
    };
  }
}
